import { useEffect, useState } from "react";
import api, { API, setPageMeta } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, shortId } from "@/lib/labels";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Package, FileText, Download, User, Paperclip } from "lucide-react";
import PayPalCheckout from "@/components/PayPalCheckout";
import { toast } from "sonner";

function downloadFile(fileId, name) {
  const token = localStorage.getItem("tap_token");
  fetch(`${API}/files/${fileId}/download`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => r.blob())
    .then((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
    });
}

function FileChips({ files }) {
  if (!files?.length) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {files.map((f) => (
        <button key={f.id} onClick={() => downloadFile(f.id, f.original_filename)}
          className="inline-flex items-center gap-1.5 rounded-full border border-[#0055FF]/40 bg-[#0055FF]/10 px-3 py-1 text-xs text-gray-200 hover:border-[#0055FF]">
          <Paperclip className="w-3 h-3" /> {f.original_filename} <Download className="w-3 h-3" />
        </button>
      ))}
    </div>
  );
}

export default function Account() {
  const { user } = useAuth();
  const [orders, setOrders] = useState(null);
  const [quotes, setQuotes] = useState(null);

  const loadQuotes = () => api.get("/quotes/mine").then(({ data }) => setQuotes(data)).catch(() => setQuotes([]));

  useEffect(() => {
    setPageMeta("Mon compte — Tenay Ain-Pression");
    api.get("/orders/mine").then(({ data }) => setOrders(data)).catch(() => setOrders([]));
    loadQuotes();
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
      <h1 className="font-heading text-4xl font-black">Bonjour, {user?.name?.split(" ")[0] || "client"}</h1>
      <p className="mt-2 text-gray-400">Retrouvez vos commandes, vos devis et vos fichiers.</p>

      <Tabs defaultValue="orders" className="mt-8">
        <TabsList className="bg-[#0A0A0A] border border-[#222]">
          <TabsTrigger value="orders" data-testid="tab-orders"><Package className="w-4 h-4 mr-2" /> Commandes</TabsTrigger>
          <TabsTrigger value="quotes" data-testid="tab-quotes"><FileText className="w-4 h-4 mr-2" /> Devis</TabsTrigger>
          <TabsTrigger value="profile" data-testid="tab-profile"><User className="w-4 h-4 mr-2" /> Profil</TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-6">
          {orders === null ? <Loader2 className="w-6 h-6 animate-spin text-[#E6007E]" />
            : orders.length === 0 ? <p className="text-gray-400">Aucune commande pour le moment.</p>
            : <div className="space-y-4">{orders.map((o) => (
                <div key={o.id} data-testid={`account-order-${o.id}`} className="rounded-2xl border border-[#222] bg-[#0A0A0A] p-5">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <p className="font-heading font-bold">Commande #{shortId(o.id)}</p>
                      <p className="text-sm text-gray-500">{new Date(o.created_at).toLocaleDateString("fr-FR")}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <StatusBadge status={o.status} type="order" />
                      <span className="font-black text-[#E6007E]">{o.total.toFixed(2)} €</span>
                    </div>
                  </div>
                  <div className="mt-3 text-sm text-gray-300">
                    {o.items.map((it, i) => <div key={i}>{it.name} ×{it.quantity} {it.color && `· ${it.color}`} {it.variant && `· ${it.variant}`}</div>)}
                  </div>
                  <FileChips files={o.files} />
                </div>
              ))}</div>}
        </TabsContent>

        <TabsContent value="quotes" className="mt-6">
          {quotes === null ? <Loader2 className="w-6 h-6 animate-spin text-[#E6007E]" />
            : quotes.length === 0 ? <p className="text-gray-400">Aucune demande de devis pour le moment.</p>
            : <div className="space-y-4">{quotes.map((q) => (
                <div key={q.id} data-testid={`account-quote-${q.id}`} className="rounded-2xl border border-[#222] bg-[#0A0A0A] p-5">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <p className="font-heading font-bold">{q.subject}</p>
                      <p className="text-sm text-gray-500">{q.category} · {new Date(q.created_at).toLocaleDateString("fr-FR")}</p>
                    </div>
                    <StatusBadge status={q.status} type="quote" />
                  </div>
                  <p className="mt-3 text-sm text-gray-300">{q.description}</p>
                  <FileChips files={q.files} />
                  {q.devis_amount > 0 && (
                    <div className="mt-4 rounded-xl border border-[#0055FF]/30 bg-[#0055FF]/5 p-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-300">Montant du devis</span>
                        <span className="font-black text-[#E6007E] text-lg">{q.devis_amount.toFixed(2)} €</span>
                      </div>
                      {q.payment_status === "paid" ? (
                        <p className="mt-2 text-sm font-bold text-green-400">✓ Devis réglé — merci !</p>
                      ) : (
                        <div className="mt-3">
                          <p className="mb-2 text-sm font-semibold text-white">Régler ce devis :</p>
                          <PayPalCheckout kind="quote" getReferenceId={async () => q.id} onSuccess={() => { toast.success("Devis réglé !"); loadQuotes(); }} />
                          <p className="mt-2 text-xs text-gray-500">Compte PayPal, carte bancaire sans compte, ou paiement en plusieurs fois si éligible.</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}</div>}
        </TabsContent>

        <TabsContent value="profile" className="mt-6">
          <div className="rounded-2xl border border-[#222] bg-[#0A0A0A] p-6 max-w-md space-y-3">
            <div><p className="text-sm text-gray-500">Nom</p><p className="font-semibold">{user?.name}</p></div>
            <div><p className="text-sm text-gray-500">E-mail</p><p className="font-semibold">{user?.email}</p></div>
            <div><p className="text-sm text-gray-500">Téléphone</p><p className="font-semibold">{user?.phone || "—"}</p></div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
